import { RouteName } from '../../../routes/routes.constants';
import { config } from '../../config';

export enum LobbiesMap {
    GAMES = 'casino',
    HOME = 'casino',
    LIVE_CASINO = 'liveCasino',

    // NOTE: only used to "tag" a subcategory, not a real lobby
    USER_RECOMMENDATIONS = 'userRecommendations',
    MARKET_RECOMMENDATIONS = 'marketRecommendations',
    FREE_TO_PLAY = 'freeToPlay',
}

/**
 * The available lobby paths
 */
enum LobbiesPaths {
    GAMES = 'games',
    LIVE_CASINO = 'live-casino',
}

/**
 * Maps the lobby value present on the game with the frontend route
 */
export const lobbiesPathsMap: Record<string, string> = {
    casino: LobbiesPaths.GAMES,
    liveCasino: LobbiesPaths.LIVE_CASINO,
};

const urlRouteMap: Record<string, RouteName> = {
    [LobbiesPaths.GAMES]: RouteName.GAMES,
    [LobbiesPaths.LIVE_CASINO]: RouteName.LIVE_CASINO,
};

const routeNameLobbyMap: Partial<Record<RouteName, string>> = {
    [RouteName.GAMES]: 'casino',
    [RouteName.HOME]: 'casino',
    [RouteName.LIVE_CASINO]: 'liveCasino',
};

export const getLobbyFromRouteName = (routeName: string) => {
    return routeNameLobbyMap[routeName as RouteName];
};

export const getPathFromLobbyMap = (lobby: string) =>
    lobbiesPathsMap[lobby] || lobby;

export const getPathFromLobby = (lobby: string) => {
    const key = Object.keys(LobbiesMap).find(
        (key) => LobbiesMap[key as keyof typeof LobbiesMap] === lobby,
    );
    return RouteName[key as keyof typeof RouteName];
};

/**
 * Tries to detect a valid lobby path from the url
 * @returns string
 */
export const getLobbyFromUrl = (): string => {
    const lobbyOnPath = getLobbyFromPathname();
    const validLobbies: string[] = Object.values(LobbiesPaths);
    const isAValidLobby = validLobbies.includes(lobbyOnPath);

    return isAValidLobby ? lobbyOnPath : LobbiesPaths.GAMES;
};

/**
 * Tries to detect lobby from the url
 * else tries to find it in the lobbiesPathsMap
 * using the first lobby passed
 * @param lobbies game lobbies
 * @returns
 */
export const getLobbyPath = (lobbies: string[]): string => {
    const lobbyFromUrl = getLobbyFromUrl();

    if (lobbyFromUrl) {
        return lobbyFromUrl;
    }

    if (lobbies && lobbies.length > 0 && lobbiesPathsMap[lobbies[0]]) {
        return lobbiesPathsMap[lobbies[0]];
    }

    return LobbiesPaths.GAMES;
};

/**
 * Gets the first lobby path from the argument array or defaults to games
 * @param lobbies game lobbies
 * @returns
 */
export const getFirstLobbyPath = (lobbies: string[]): string => {
    if (lobbies && lobbies.length > 0 && lobbiesPathsMap[lobbies[0]]) {
        return lobbiesPathsMap[lobbies[0]];
    }

    return LobbiesPaths.GAMES;
};

/**
 * Detects which route to use depending on url
 * e.g. if live-casino is in url it will return RouteName.LIVE_CASINO
 * @returns RouteName
 */
export const detectRouteNameFromUrl = (): RouteName => {
    const lobby = getLobbyFromPathname();
    return urlRouteMap[lobby] ?? RouteName.GAMES;
};

function getLobbyFromPathname() {
    const singleLanguage = config.languages.available.length === 1;
    const lobbyIndex = singleLanguage ? 1 : 2;
    return window.location.pathname.split('/')[lobbyIndex];
}
